import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Clock, Trophy } from "@phosphor-icons/react";

export default function QuizRunner() {
  const { t } = useTranslation();
  const { quiz_id } = useParams();
  const nav = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // question_id -> [strings]
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const submit = useCallback(async () => {
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const payload = { quiz_id, answers };
      const { data } = await api.post(`/lms/quizzes/${quiz_id}/attempt`, payload);
      setResult(data);
      toast.success(`${t("lms.your_score")}: ${data.score}/${data.total}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }, [quiz_id, answers, submitting, result, t]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lms/quizzes/${quiz_id}/start`);
        setQuiz(data.quiz);
        setQuestions(data.questions);
        if (data.quiz?.time_limit_min) setTimeLeft(data.quiz.time_limit_min * 60);
      } catch {
        toast.error("Quiz not found");
        nav("/app/lms");
      }
    })();
  }, [quiz_id, nav]);

  useEffect(() => {
    if (timeLeft === null || result) return;
    if (timeLeft <= 0) { submit(); return; }
    const tm = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(tm);
  }, [timeLeft, result, submit]);

  if (!quiz) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">{t("app.loading")}</div>;
  if (!questions.length) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6">
      <div className="text-sm text-muted-foreground">No questions in this quiz yet.</div>
      <Button onClick={() => nav("/app/lms")} variant="outline" data-testid="quiz-back-btn">{t("lms.back_to_lms")}</Button>
    </div>
  );

  if (result) {
    const pass = result.passed;
    return (
      <div className="min-h-screen bg-secondary/30 p-6" data-testid="quiz-result">
        <div className="max-w-2xl mx-auto">
          <div className={`eos-card border-2 ${pass ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
            <div className="flex items-center gap-3">
              <Trophy size={32} weight="duotone" className={pass ? "text-emerald-600" : "text-rose-600"}/>
              <div>
                <div className="eos-overline">{quiz.title}</div>
                <div className="font-heading font-extrabold text-3xl mt-1">{pass ? t("lms.passed") : t("lms.failed")}</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[11px] text-muted-foreground uppercase">{t("lms.your_score")}</div>
                <div className="font-mono-eos text-2xl font-bold mt-1">{result.score}/{result.total}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase">Percentage</div>
                <div className="font-mono-eos text-2xl font-bold mt-1">{result.pct}%</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground uppercase">Pass mark</div>
                <div className="font-mono-eos text-2xl font-bold mt-1">{quiz.passing_score}%</div>
              </div>
            </div>
          </div>

          <div className="mt-4 eos-card" data-testid="quiz-review">
            <div className="font-heading font-bold mb-3">{t("lms.review_answers")}</div>
            <div className="space-y-2">
              {questions.map((q, i) => {
                const correct = result.graded_per_q?.[q.question_id];
                return (
                  <div key={q.question_id} className={`p-3 border rounded-md ${correct ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"}`} data-testid={`review-${q.question_id}`}>
                    <div className="flex items-start gap-2">
                      {correct ? <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5"/> : <XCircle size={16} weight="fill" className="text-rose-600 mt-0.5"/>}
                      <div className="flex-1">
                        <div className="text-[11px] text-muted-foreground">Q{i + 1} · {q.marks} {t("lms.marks")}</div>
                        <div className="font-medium text-sm mt-0.5">{q.text}</div>
                        <div className="text-[12px] text-muted-foreground mt-1">Your answer: {(answers[q.question_id] || []).join(", ") || "—"}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button onClick={() => nav("/app/lms")} className="bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="quiz-done-btn">{t("lms.back_to_lms")}</Button>
          </div>
        </div>
      </div>
    );
  }

  const q = questions[idx];
  const selected = answers[q.question_id] || [];
  const setSelected = (vals) => setAnswers((a) => ({ ...a, [q.question_id]: vals }));
  const toggle = (opt) => {
    if (q.type === "MSQ") setSelected(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);
    else setSelected([opt]);
  };
  const mmss = timeLeft !== null ? `${Math.floor(timeLeft / 60).toString().padStart(2, "0")}:${(timeLeft % 60).toString().padStart(2, "0")}` : null;

  return (
    <div className="min-h-screen bg-secondary/30 p-6" data-testid="quiz-runner">
      <div className="max-w-2xl mx-auto">
        <div className="eos-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eos-overline">{quiz.title}</div>
              <div className="text-[12px] text-muted-foreground mt-0.5">{t("lms.question_n", { n: idx + 1, total: questions.length })}</div>
            </div>
            {mmss && (
              <div className="inline-flex items-center gap-1.5 text-sm font-mono-eos px-3 py-1.5 border border-border rounded-md" data-testid="quiz-timer">
                <Clock size={14}/> {mmss}
              </div>
            )}
          </div>

          <div className="h-1 bg-border rounded-full overflow-hidden mb-4">
            <div className="h-full bg-[hsl(20_90%_48%)] transition-all" style={{ width: `${((idx + 1) / questions.length) * 100}%` }}/>
          </div>

          <div className="font-heading font-bold text-lg mb-1">{q.text}</div>
          <div className="text-[11px] text-muted-foreground mb-4">{q.type} · {q.marks} {t("lms.marks")}</div>

          {(q.type === "MCQ" || q.type === "MSQ" || q.type === "TF") && q.options?.length > 0 && (
            <div className="space-y-2" data-testid="quiz-options">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => toggle(opt)}
                  className={`w-full text-left p-3 border rounded-md transition-colors ${selected.includes(opt) ? "border-[hsl(244_53%_20%)] bg-[hsl(244_53%_20%)] text-white" : "border-border bg-white hover:bg-secondary/40"}`}
                  data-testid={`quiz-option-${i}`}
                >
                  <span className="font-mono-eos text-[11px] mr-2 opacity-70">{String.fromCharCode(65 + i)}.</span>
                  {opt}
                </button>
              ))}
            </div>
          )}
          {(q.type === "SHORT" || q.type === "NUMERIC") && (
            <input
              type={q.type === "NUMERIC" ? "number" : "text"}
              value={selected[0] || ""}
              onChange={(e) => setSelected([e.target.value])}
              placeholder={t("lms.enter_answer")}
              className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:border-foreground/40"
              data-testid="quiz-input"
            />
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button onClick={() => setIdx(idx - 1)} disabled={idx === 0} variant="outline" data-testid="quiz-prev-btn">
              <ArrowLeft size={14} className="mr-1"/> {t("lms.previous")}
            </Button>
            {idx < questions.length - 1 ? (
              <Button onClick={() => setIdx(idx + 1)} className="bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="quiz-next-btn">
                {t("lms.next")} <ArrowRight size={14} className="ml-1"/>
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="bg-[hsl(20_90%_48%)] hover:bg-[hsl(20_90%_40%)]" data-testid="quiz-submit-btn">
                {submitting ? "…" : t("lms.submit")}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3 text-center">
          <button onClick={() => nav("/app/lms")} className="text-[12px] text-muted-foreground hover:text-foreground underline" data-testid="quiz-exit-btn">
            {t("lms.back_to_lms")}
          </button>
        </div>
      </div>
    </div>
  );
}
